import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { vouchersApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { AuditTimeline } from '../../components/shared/AuditTimeline';
import { VoucherPrintTemplate } from '../../components/voucher/VoucherPrintTemplate';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  getPaymentMethodLabel,
  getPaymentTypeLabel,
} from '../../lib/utils';
import {
  ArrowLeft,
  CheckCircle,
  XCircle,
  Building2,
  User,
  Calendar,
  CreditCard,
  Banknote,
  Printer,
  FileText,
  Download,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';

export const FinanceHeadVoucherDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [voucher, setVoucher] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [actionNote, setActionNote] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [voucherData, logsData] = await Promise.all([
          vouchersApi.getById(id),
          vouchersApi.getAuditLogs(id),
        ]);
        setVoucher(voucherData);
        setAuditLogs(logsData);
      } catch (error) {
        toast.error('Voucher not found');
        navigate('/finance-head/all');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  // Check if this Finance Head can approve this voucher
  // Finance Head can approve SUBMITTED vouchers if they are the assigned manager
  const canApproveAsManager = voucher?.status === 'SUBMITTED' && 
                              voucher?.assignedManagerId === user?.id;

  const handleApprove = async () => {
    setProcessing(true);
    try {
      // Use manager approve endpoint for SUBMITTED vouchers
      const updated = await vouchersApi.managerApprove(id, actionNote);
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      toast.success('Voucher approved - sent to Internal Audit');
      setShowApproveDialog(false);
      setActionNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve voucher');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!actionNote.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setProcessing(true);
    try {
      // Use manager reject endpoint for SUBMITTED vouchers
      const updated = await vouchersApi.managerReject(id, actionNote);
      setVoucher(updated);
      const logs = await vouchersApi.getAuditLogs(id);
      setAuditLogs(logs);
      toast.success('Voucher rejected');
      setShowRejectDialog(false);
      setActionNote('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject voucher');
    } finally {
      setProcessing(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-48 bg-gray-100 rounded animate-pulse" />
            ))}
          </div>
          <div className="h-96 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!voucher) return null;

  return (
    <div className="space-y-6" data-testid="finance-head-voucher-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-heading font-semibold tracking-tight font-mono">
                {voucher.voucherNo || 'Draft Voucher'}
              </h1>
              <StatusBadge status={voucher.status} />
            </div>
            <p className="text-muted-foreground text-sm mt-1">
              Created {formatDateTime(voucher.createdAt)} by {voucher.createdByName}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 print:hidden">
          <Button variant="outline" onClick={handlePrint} data-testid="print-button">
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          {canApproveAsManager && (
            <>
              <Button
                onClick={() => setShowRejectDialog(true)}
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                data-testid="reject-button"
              >
                <XCircle className="w-4 h-4 mr-2" /> Reject
              </Button>
              <Button
                onClick={() => setShowApproveDialog(true)}
                className="bg-green-600 hover:bg-green-700"
                data-testid="approve-button"
              >
                <CheckCircle className="w-4 h-4 mr-2" /> Approve
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Voucher Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Voucher Details</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Date</p>
                    <p className="font-medium">{formatDate(voucher.date)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Department</p>
                    <p className="font-medium">{voucher.department}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Building2 className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Branch</p>
                    <p className="font-medium">{voucher.branch}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <User className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Beneficiary</p>
                    <p className="font-medium">{voucher.beneficiaryName}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Payment Method</p>
                    <p className="font-medium">{getPaymentMethodLabel(voucher.paymentMethod)}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Banknote className="w-5 h-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Payment Type</p>
                    <p className="font-medium">{getPaymentTypeLabel(voucher.paymentType)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Line Items */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Line Items</CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document No.</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="text-right">Invoice Amount</TableHead>
                    <TableHead className="text-right">Payable Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {voucher.items?.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell className="font-mono">{item.invoiceNo || '-'}</TableCell>
                      <TableCell className="text-muted-foreground">{item.note || '-'}</TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.amount, item.currency || voucher.currency)}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        {formatCurrency(item.payableAmount || 0, item.currency || voucher.currency)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <div className="flex justify-end pt-4 border-t mt-4">
                <div className="text-right">
                  <p className="text-sm text-muted-foreground">Total Payable Amount</p>
                  <p className="text-2xl font-mono font-semibold text-[#6a5da4]">
                    {formatCurrency(voucher.amountTotal, voucher.currency)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Attachments */}
          {voucher.attachments && voucher.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-heading">Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {voucher.attachments.map((attachment) => {
                    const [filename, originalName] = attachment.split('|');
                    return (
                      <div
                        key={filename}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                          <span className="text-sm">{originalName || filename}</span>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/${filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-[#6a5da4] hover:underline text-sm flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Additional Attachments */}
          {voucher.additionalAttachments?.length > 0 && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-lg font-heading text-blue-700">Additional Attachments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {voucher.additionalAttachments.map((attachment, index) => {
                    const parts = attachment.split('|');
                    const filename = parts[0];
                    const originalName = parts[1] || filename;
                    const uploadedAt = parts[2];
                    return (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100"
                      >
                        <div className="flex items-center gap-3">
                          <FileText className="w-5 h-5 text-blue-600" />
                          <div>
                            <span className="text-sm font-medium">{originalName}</span>
                            {uploadedAt && (
                              <p className="text-xs text-muted-foreground">
                                Uploaded: {formatDateTime(uploadedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        <a
                          href={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/${voucher.id}/additional/${filename}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline text-sm flex items-center gap-1"
                        >
                          <Download className="w-4 h-4" /> Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Amount Summary - Purple Card */}
          <Card className="bg-[#6a5da4] text-white">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 mb-4">
                <Banknote className="w-8 h-8 opacity-80" />
                <div>
                  <p className="text-sm opacity-80">Total Amount</p>
                  <p className="text-3xl font-mono font-semibold">
                    {formatCurrency(voucher.amountTotal, voucher.currency)}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-white/20">
                <div>
                  <p className="text-xs opacity-60 uppercase">Items</p>
                  <p className="font-semibold">{voucher.items?.length || 0}</p>
                </div>
                <div>
                  <p className="text-xs opacity-60 uppercase">Currency</p>
                  <p className="font-semibold">{voucher.currency}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Approval Workflow */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Approval Workflow</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {voucher.assignedManagerName && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Assigned Manager</p>
                  <p className="font-medium">{voucher.assignedManagerName}</p>
                </div>
              )}
              {voucher.assignedFinanceHeadName && (
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Assigned Finance Head</p>
                  <p className="font-medium">{voucher.assignedFinanceHeadName}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity Log */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-heading">Activity Log</CardTitle>
            </CardHeader>
            <CardContent>
              <AuditTimeline logs={auditLogs} />
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Voucher</DialogTitle>
            <DialogDescription>
              Approve this voucher to proceed to Accounts for payment processing.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="approve-note">Note (Optional)</Label>
              <Textarea
                id="approve-note"
                placeholder="Add any notes for approval..."
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleApprove} disabled={processing} className="bg-green-600 hover:bg-green-700">
              {processing ? 'Approving...' : 'Approve'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Voucher</DialogTitle>
            <DialogDescription>
              Reject this voucher and provide a reason.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reject-note">Reason for Rejection *</Label>
              <Textarea
                id="reject-note"
                placeholder="Provide reason for rejection..."
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                required
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)} disabled={processing}>
              Cancel
            </Button>
            <Button onClick={handleReject} disabled={processing || !actionNote.trim()} variant="destructive">
              {processing ? 'Rejecting...' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Template */}
      <VoucherPrintTemplate voucher={voucher} />
    </div>
  );
};

export default FinanceHeadVoucherDetailPage;
